import { create } from 'zustand';

interface AddTopicGroupFlowState {
  name: string;
  selectedCategoryIds: number[];
  selectedSubcategoryIds: number[];
  selectedTopicIds: string[];
}

type TopicGroupAction = {
  setName: (name: string) => void;
  setCategoryIds: (categoryIds: number[]) => void;
  setSubcategoryIds: (subcategoryIds: number[]) => void;
  setTopicIds: (topicIds: string[]) => void;
  clear: () => void;
};

interface AddTopicGroupActions {
  setTopicGroupState: (arg: (operation: TopicGroupAction) => void) => void;
  openModal: () => void;
  closeModal: () => void;
}

interface AddTopicGroupState {
  topicGroupState: AddTopicGroupFlowState;
  isOpen: boolean;
}

type AddTopicGroupStore = {
  actions: AddTopicGroupActions;
  modalState: AddTopicGroupState;
};

const initialTopicGroupState: AddTopicGroupFlowState = {
  name: '',
  selectedCategoryIds: [],
  selectedSubcategoryIds: [],
  selectedTopicIds: []
};

/**
 * Simple Zustand store for the Create Topic Group modal actions and state.
 * Used to open or populate the Create Topic Group modal from anywhere in the app.
 */
const useAddTopicGroupStore = create<AddTopicGroupStore>((set) => ({
  modalState: {
    topicGroupState: initialTopicGroupState,
    isOpen: false
  },
  actions: {
    setTopicGroupState: (arg) => {
      set((state) => {
        const operations: TopicGroupAction = {
          setName: (name: string) =>
            set({
              modalState: {
                ...state.modalState,
                topicGroupState: {
                  ...state.modalState.topicGroupState,
                  name
                }
              }
            }),
          setTopicIds: (topicIds) =>
            set({
              modalState: {
                ...state.modalState,
                topicGroupState: {
                  ...state.modalState.topicGroupState,
                  selectedTopicIds: topicIds
                }
              }
            }),
          setCategoryIds: (categoryIds) =>
            set({
              modalState: {
                ...state.modalState,
                topicGroupState: {
                  ...state.modalState.topicGroupState,
                  selectedCategoryIds: categoryIds
                }
              }
            }),
          setSubcategoryIds: (subcategoryIds) =>
            set({
              modalState: {
                ...state.modalState,
                topicGroupState: {
                  ...state.modalState.topicGroupState,
                  selectedSubcategoryIds: subcategoryIds
                }
              }
            }),
          clear: () =>
            set({
              modalState: {
                ...state.modalState,
                topicGroupState: initialTopicGroupState
              }
            })
        };
        arg(operations);
        return {};
      });
    },

    openModal: () => set((state) => ({ modalState: { ...state.modalState, isOpen: true } })),
    closeModal: () => set((state) => ({ modalState: { ...state.modalState, isOpen: false } }))
  }
}));

export const useAddTopicGroupStoreState = () => useAddTopicGroupStore((state) => state.modalState);
export const useAddTopicGroupStoreActions = () => useAddTopicGroupStore((state) => state.actions);
